import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ACCOUNT_MANAGEMENT } from "@consts";
import { wrapAccountRow, unwrapFieldValues } from "../AccountEditor.utils";
import useAppStore from "src/stores/appStore";
import {
  editAccountSelector,
  getAccountsAdminSelector,
  managementAccountsSelector,
  accountManagementErrorSelector,
  accountManagementErrorTextSelector,
  setAccountManagementErrorSelector
} from "src/stores/selectors/appSelectors";

const useAccountSettings = () => {
  const navigate = useNavigate();
  const { accountRowId } = useParams();
  const [searchParams] = useSearchParams();

  const managementAccounts = useAppStore(managementAccountsSelector);
  const getAccounts = useAppStore(getAccountsAdminSelector);
  const editAccount = useAppStore(editAccountSelector);
  const error = useAppStore(accountManagementErrorSelector);
  const errorText = useAppStore(accountManagementErrorTextSelector);
  const setError = useAppStore(setAccountManagementErrorSelector);

  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [saveVersion, setSaveVersion] = useState(0);

  useEffect(() => {
    if (!managementAccounts) {
      getAccounts();
    }
  }, [managementAccounts, getAccounts]);

  useEffect(() => {
    if (isSaving && !error) {
      setIsSaving(false);
      setIsConfirmationOpen(false);
      setSaveVersion(v => v + 1);
    }
  }, [managementAccounts, error]);

  useEffect(() => {
    if (error) {
      setIsSaving(false);
      setIsConfirmationOpen(false);
    }
  }, [error]);

  const accountRow = managementAccounts?.find(account => account.id === accountRowId);
  const accountData = accountRow ? wrapAccountRow(accountRow, "edit") : null;

  const handleSubmit = (data) => {
    const flat = unwrapFieldValues(data);
    setIsSaving(true);
    editAccount({
      id: data.id,
      name: flat.name,
      accountId: flat.accountId,
      timezone: flat.timezone,
      sftpIpList: flat.sftpIpList,
      publicKey: flat.publicKey
    });
  };

  const handleDiscard = () => {
    setIsConfirmationOpen(false);
    navigate(`/admin/${ACCOUNT_MANAGEMENT}`, { replace: true });
  };

  return {
    managementAccounts,
    accountRow,
    accountData,
    isSaving,
    isConfirmationOpen,
    saveVersion,
    error,
    errorText,
    setError,
    searchParams,
    handleSubmit,
    handleDiscard,
    openConfirmation: () => setIsConfirmationOpen(true),
    closeConfirmation: () => setIsConfirmationOpen(false)
  };
};

export default useAccountSettings;
